/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
	Button,
	Card,
	Skeleton,
	Stack,
	Typography,
	Link,
	Box,
	FormControl,
	FormLabel,
} from '@mui/material';
import { useGetClientPoliciesQuery } from '@/features/clients/clientsApi';
import EmailIcon from '@mui/icons-material/Email';
import { useDispatch, useSelector } from 'react-redux';
import { NavigateInsightButton } from '@/components/buttons/NavigateInsightButton';
import { GoogleMaps } from '@/components/GoogleMapsLocation';
import { ClientTypeSelect } from '@/components/selects/ClientTypeSelect';
import { useParams } from 'react-router-dom';
import { useObjectContext } from '@/hooks/useObjectContext';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { ProspectStageSelect } from '@/components/selects/prospectStatusSelect';
import { ChangeAssigneeField } from '@/components/cards/DealCardValues/ChangeAssigneeField';
import { ProspectAmountValue } from '@/components/ProspectAmountValue';
import { safelyParseDate } from '@/utils/date';
import { formatDate } from '@/utils/constants';
import { useListContactsQuery } from '@/features/contacts/contactsApi';
import AskAiButton from '@/components/buttons/viewHeaderButtons/AskAiButton.jsx';
import ReferralPartnerField from '@/components/fields/ReferralPartnerField';
import { useGetDealsByClientIdQuery } from '@/features/deals/dealsApi';
import { ReferralStatus } from '@/components/cards/DealCardValues/ReferralStatus';
import { useReferralStatusFields } from '@/hooks/useReferralStatusFields';

const Address = ({ insight }) => {
	const formattedAddress = useMemo(() => {
		if (!insight?.OneLineAddress) return [];
		const data = insight.OneLineAddress.split(',').map((part) => part.trim());
		return data.reduce((acc, part, i) => {
			if (part === insight?.Postcode) {
				acc[i - 1] = `${acc[i - 1]} ${part}`;
			} else {
				acc.push(part);
			}
			return acc;
		}, []);
	}, [insight?.OneLineAddress, insight?.Postcode]);

	return (
		<Stack>
			{formattedAddress.length > 0 &&
				formattedAddress.map((part, i) => (
					<Typography key={i} variant='subtitle2' sx={{ color: 'grey' }}>
						{part}
					</Typography>
				))}
		</Stack>
	);
};

export const AdviserAboutCard = () => {
	const { clientId } = useParams();
	const { clientRow, insight, broker } = useObjectContext();

	const insightId = clientRow?.insightId;

	const { data: policies, isSuccess: isPolicySuccess } = useGetClientPoliciesQuery(
		{ insightId, clientId },
		{ skip: !insightId && !clientId },
	);

	// Use the resolved row's id: the URL clientId may be a legacy hubspotId.
	const { currentData: clientDeals = [] } = useGetDealsByClientIdQuery(clientRow?.clientId, {
		skip: !clientRow?.clientId,
	});
	const dealIds = useMemo(
		() =>
			clientDeals
				.filter((deal) => deal.pipeline !== process.env.REACT_APP_PIPELINE_CLAIMS)
				.map((deal) => deal.dealId),
		[clientDeals],
	);
	const claimIds = useMemo(
		() =>
			clientDeals
				.filter((deal) => deal.pipeline === process.env.REACT_APP_PIPELINE_CLAIMS)
				.map((deal) => deal.dealId),
		[clientDeals],
	);

	const hideClientType = useMemo(
		() =>
			!isPolicySuccess
				? true
				: (policies ?? []).length > 0 || claimIds.length > 0 || dealIds.length === 0,
		[dealIds, claimIds, policies, isPolicySuccess],
	);

	const { data: contacts, ...contactQuery } = useListContactsQuery(clientId, {
		skip: !clientId,
		selectFromResult: ({ data, ...rest }) => {
			const contacts = data ?? [];
			return {
				...rest,
				data: contacts,
			};
		},
	});

	const { getFeatureFlags } = useFeatureFlagContext();
	const showProspects = Boolean(getFeatureFlags('prospects')?.active);
	const showProspects2 = Boolean(getFeatureFlags('prospects2')?.active);
	const showEmail = Boolean(getFeatureFlags('emails')?.active);
	const showUnlayerEmails = Boolean(getFeatureFlags('unlayerEmails')?.active);
	const salesTeamChange = Boolean(getFeatureFlags('salesTeamChange')?.active);

	const showProspectsFeature = showProspects || showProspects2;
	const referralFields = useReferralStatusFields();

	const mainContact = useMemo(() => contacts?.find((c) => c.isMainContact), [contacts]);

	const dispatch = useDispatch();

	const AdviserEntry = useCallback(
		({ header, value = '-', isEmail = false, isPhone = false }) => {
			return (
				<Stack>
					<FormLabel>{header}</FormLabel>
					{contactQuery.isLoading || contactQuery.isUninitialized ? (
						<Skeleton width={'100%'} />
					) : (
						<Typography variant='subtitle2' sx={{ color: 'grey' }}>
							{isEmail && value ? (
								<Link
									href={`mailto:${value}`}
									underline='hover'
									sx={(theme) => ({
										color: 'inherit',
										'&:hover': {
											color: theme.palette.primary.main,
										},
									})}
								>
									{value}
								</Link>
							) : isPhone && value ? (
								<Link
									href={`tel:${value.replace(/\s/g, '')}`}
									underline='hover'
									sx={(theme) => ({
										color: 'inherit',
										'&:hover': {
											color: theme.palette.primary.main,
										},
									})}
								>
									{value}
								</Link>
							) : (
								value
							)}
						</Typography>
					)}
				</Stack>
			);
		},
		[contactQuery.isLoading, contactQuery.isUninitialized],
	);

	const fullName = mainContact?.Name ?? mainContact?.Addressee ?? 'Unknown';

	const birthDate = useMemo(() => {
		const date = safelyParseDate(mainContact?.BirthDate);
		if (date) {
			return formatDate(date);
		}
	}, [mainContact?.BirthDate]);

	const headerPrefix = clientRow?.isProspect ? 'Prospect' : 'Client';

	return (
		<Card variant='about-card' sx={{ p: 3, borderTopRightRadius: 0, borderTopLeftRadius: 0 }}>
			<Stack spacing={3}>
				<GoogleMaps address={insight?.OneLineAddress} />
				<Stack spacing={1}>
					<NavigateInsightButton insightId={clientRow?.insightId} />
					{(showEmail || showUnlayerEmails) && (
						<>
							{showEmail && !showUnlayerEmails && (
								<Button
									variant='tab_header'
									startIcon={<EmailIcon />}
									sx={{ width: '100%' }}
									onClick={() => dispatch(openDialog('email'))}
								>
									{'Send Email'}
								</Button>
							)}
							{showUnlayerEmails && (
								<Button
									variant='tab_header'
									startIcon={<EmailIcon />}
									sx={{ width: '100%' }}
									onClick={() =>
										dispatch(
											openDialog({
												dialogName: 'unlayerEmail',
												data: {
													clientLocked: true,
													client: clientRow ?? null,
												},
											}),
										)
									}
								>
									{'Send Email'}
								</Button>
							)}
						</>
					)}
					<AskAiButton />
				</Stack>

				<Typography variant='subtitle1'>{`${headerPrefix} Details`}</Typography>
				<ChangeAssigneeField companySalesTeamChange={true} disabled={!salesTeamChange} />
				{/*deals?.length greater than 0 means deal has business show client type */}
				{showProspectsFeature && !hideClientType && (
					<FormControl fullWidth>
						<FormLabel>{'Client Type'}</FormLabel>
						<ClientTypeSelect minimal={true} />
					</FormControl>
				)}
				{showProspectsFeature && clientRow?.isProspect && (
					<Stack spacing={3}>
						<Box>
							<FormLabel>{'Prospect stage'}</FormLabel>
							<ProspectStageSelect minimal={true} />
						</Box>
						<Box>
							<FormLabel>{'Referral partner'}</FormLabel>
							<ReferralPartnerField minimal={true} />
						</Box>

						<ProspectAmountValue />
						{referralFields.map((field) => (
							<ReferralStatus key={field.name} field={field} />
						))}
					</Stack>
				)}
				<AdviserEntry header={`${headerPrefix} name`} value={clientRow?.name} />
				<Stack>
					<FormLabel>{`${headerPrefix} address`}</FormLabel>
					<Address insight={insight} />
				</Stack>
				{/* <AdviserEntry header={`${headerPrefix} address`} value={insight?.OneLineAddress} /> */}

				<AdviserEntry header={'Primary Contact'} value={fullName} />
				<AdviserEntry
					header={'Primary Contact Phone'}
					value={mainContact?.Telephone}
					isPhone={true}
				/>
				<AdviserEntry
					header={'Alternative Contact Phone'}
					value={mainContact?.Telephone2}
					isPhone={true}
				/>
				<AdviserEntry
					header={'Primary Contact Email'}
					value={mainContact?.Email}
					isEmail={true}
				/>
				<AdviserEntry header={'Broker Group'} value={broker?.name} />
				<AdviserEntry header={'Date of Birth'} value={birthDate} />
			</Stack>
		</Card>
	);
};
AdviserAboutCard.displayName = 'AdviserAboutCard';
